import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, EyeOff, LoaderCircle, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { StaffError, staffRequest } from './ReflectStaffShell';
import { StatusChip } from './StaffControls';

const themeTitle = (key = '') => key
  .split('_')
  .filter(Boolean)
  .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
  .join(' ');

const ProfileDrawer = ({ classId, onClose, student }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!student) {
      setProfile(null);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError('');
    staffRequest(`/api/reflect/classes/${classId}/roster/${student.id}/profile`)
      .then((data) => {
        if (active) setProfile(data);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [classId, student]);

  const groups = useMemo(() => (profile?.answers || []).reduce((result, answer) => {
    const key = answer.theme_key || 'other';
    if (!result[key]) result[key] = [];
    result[key].push(answer);
    return result;
  }, {}), [profile]);

  if (!student) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/70" role="presentation">
      <button
        type="button"
        className="min-h-full flex-1 cursor-default"
        onClick={onClose}
        aria-label="Close student profile"
      />
      <aside
        className="h-full w-full max-w-2xl overflow-y-auto border-l border-white/10 bg-[#090b0c]
          p-5 shadow-2xl sm:p-7"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-title"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan">Student profile</p>
            <h2 id="profile-title" className="mt-1 font-heading text-3xl font-black">
              {student.display_name}
            </h2>
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="min-h-11 min-w-11 rounded-full text-white/60 hover:bg-white/10 hover:text-white"
            aria-label="Close profile"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {loading && (
          <div className="flex min-h-64 items-center justify-center">
            <LoaderCircle className="h-8 w-8 animate-spin text-cyan" aria-label="Loading profile" />
          </div>
        )}

        {!loading && (
          <div className="mt-6 space-y-5">
            <StaffError>{error}</StaffError>
            {profile && (
              <>
                <div className="grid gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-black uppercase tracking-wide text-white/35">Artist alias</p>
                    <p className="mt-1 text-lg font-bold">{profile.alias || 'Not claimed yet'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-black uppercase tracking-wide text-white/35">Status</p>
                    <div className="mt-2"><StatusChip status={profile.status} /></div>
                  </div>
                </div>

                {Object.keys(groups).length ? Object.entries(groups).map(([theme, answers]) => (
                  <section key={theme} className="rounded-2xl border border-white/10 bg-white/[0.025] p-5">
                    <h3 className="font-heading text-xl font-black">{themeTitle(theme)}</h3>
                    <div className="mt-4 divide-y divide-white/10">
                      {answers.map((answer, index) => (
                        <article
                          key={`${answer.session_id}-${index}`}
                          className="py-4 first:pt-0 last:pb-0"
                        >
                          <p className="font-bold leading-relaxed text-white/85">
                            {answer.question_text}
                          </p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {Boolean(answer.private) && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full border border-white/15
                                  px-2.5 py-1 text-xs font-bold text-white/60"
                              >
                                <EyeOff className="h-3.5 w-3.5" />
                                Private: only staff see this
                              </span>
                            )}
                            {Boolean(answer.care_flagged) && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full border border-red-400/40
                                  bg-red-500/10 px-2.5 py-1 text-xs font-bold text-red-100"
                              >
                                <AlertTriangle className="h-3.5 w-3.5" />
                                Care flagged
                              </span>
                            )}
                            {Boolean(answer.skipped) && (
                              <span
                                className="rounded-full border border-white/15 px-2.5 py-1 text-xs
                                  font-bold text-white/50"
                              >
                                Skipped
                              </span>
                            )}
                          </div>
                          {!answer.skipped && (
                            <p className="mt-3 whitespace-pre-wrap leading-relaxed text-white/65">
                              {answer.answer_text || 'No answer saved'}
                            </p>
                          )}
                        </article>
                      ))}
                    </div>
                  </section>
                )) : (
                  <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center text-white/50">
                    No answers saved yet.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </aside>
    </div>
  );
};

export default ProfileDrawer;
